// Modo "Replicar una foto" — pantalla propia (ruta `/replicar-foto`).
// Ver ADR-2026-09-10-modo-replicar-foto-belleza (cómo funciona) y
// ADR-2026-09-13-replicar-una-foto-sale-del-rubro-belleza (por qué lo ve todo el mundo).
//
// El usuario elige (o sube) una foto que hace de PLANTILLA y Atlas genera esa misma
// escena con los cambios que pida; si además elige UNO O VARIOS productos de su
// catálogo, la escena sale con esos productos adentro. Las plantillas subidas se quedan en una
// colección propia de la empresa (`plantillasPropiasApi`), así la segunda generación
// ya no pasa por el file picker: ésa es la mitad del modo.
//
// Los productos son OPCIONALES y se eligen desde su card
// (ADR-2026-09-13-replicar-una-foto-arranca-en-configurar-y-el-producto-es-opcional):
// el paso 1 de la grilla de productos que había antes se sacó, porque muchas veces lo
// que se quiere cambiar de la foto es el contexto y no el producto. Pueden ser VARIOS
// (ADR-2026-09-14-replicar-una-foto-con-varios-productos): el picker es de selección
// múltiple con su pie de confirmar, y cada producto viaja como su propia referencia.
//
// EN MOBILE el modo tiene DOS pantallas
// (ADR-2026-09-14-replicar-foto-mobile-abre-con-la-foto): la primera es la del modo
// —portada, qué hay que cargar y la card que abre el picker de plantillas— y la segunda
// es la configuración. La bisagra es la FOTO, que es lo único obligatorio: elegirla o
// subirla pasa sola a la segunda. En DESKTOP no existe: allá la portada se ve en la
// columna de muestra, al lado del panel, así que una pantalla previa repetiría lo mismo.
//
// Es el inverso del modo Plantillas: allá la escena la cura Atlas y el usuario
// pone el producto; acá la escena la trae él. Por eso NO comparte pantalla ni
// datos con `PlantillasBelleza` — una plantilla del catálogo es una fila de
// `templates` con `base_prompt`, y acá es un archivo que subió el usuario.
//
// CÓMO GENERA: `generateFreeformImage` (`modo: 'creativo_plus'`), el mismo camino
// que usa el Asistente cuando alguien adjunta una imagen y pide "mi producto en
// esta escena". Las referencias van `[plantilla, producto]` y el prompt de
// `replicarFoto.ts` las nombra en ese orden — es lo único que le dice al modelo
// cuál es cuál, así que si acá se cambia el orden, allá se cambia el texto.
//
// SIN gate de rubro: la card está en `modesCatalog` sin `soloBelleza` y esta
// pantalla no rebota a nadie. Nació para Belleza, pero nada del modo mira el rubro
// —la escena la trae el usuario— así que lo ve toda la app
// (ADR-2026-09-13-replicar-una-foto-sale-del-rubro-belleza).
//
// La anatomía (shell, pantalla de mobile, columna de resultados, cards y paneles)
// es la de `MostrarComoFuncionaBellezaMode`, que era el modo más nuevo cuando se
// escribió. Lo que cambia es lo que se elige.
//
// OJO con los colores: los tokens `brand-*` no declaran `<alpha-value>`, así que
// `bg-brand-primary/10` no emite regla (queda transparente). Para los azules con
// alpha va el hex arbitrario (`bg-[#1644EB]/10`) y para los grises, `text-black/<n>`.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ImagePlus,
  Images,
  Loader2,
  Package,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import { useSidebar } from '../../../context/SidebarContext';
import { useVolverAtras } from '../../../hooks/useVolverAtras';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import CachedImg from '../../../components/CachedImg';
import LoadingAnimation from '../../../components/LoadingAnimation';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { CreditBadge } from '../../../components/CreditBadge';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import costoDeGeneracion from '../../../lib/costoGeneracion';
import { UnsupportedImageFormatError } from '../../../utils/heicConvert';
import type { EstadoDeRecrearImagen, PinParaRecrear } from '../components/subidasParaRecrear';
import { pinDeEntrada } from '../components/pinterestParaRecrear';
import {
  FREEFORM_MAX_CANTIDAD,
  formatSlugToAspect,
  generateFreeformImage,
} from '../agentGenerate';
import {
  ProductsContext,
  ProductsLoadedContext,
} from '../productsContext';
import { fetchUserProducts, getProductFullImageUrl } from '../productsApi';
import { getFormatLabel, type MockProductV2 } from '../mockData';
import { FormatPickerGrid } from '../FormatPicker';
import { PickProductPanel } from '../components/PickProductPanel';
import { PasoUnoMobile } from './shared/PasoUnoMobile';
// La modelo: el MISMO picker y la misma `ModelSelection` que el modo Modelo
// (ADR-2026-09-10-el-picker-de-modelo-es-el-mismo-que-el-del-modo-modelo).
import { modelSelectionLabel, type ModelSelection } from '../ModelPicker';
import { ModelPickerInline, type ModelPickerView } from '../modelo/ModelPickerInline';
import { fetchUserModels, firmarFotoDeModelo, type ModelV2 } from '../modelsApi';
import { descripcionModeloEnPalabras } from '../mostrarComoFuncionaBelleza';
import type { ReferenciaEtiquetada } from '../referenciasDeGeneracion';
import { SubirProductoDrawer } from '../components/SubirProductoDrawer';
import { ShellModoRedisenado } from './shared/ShellModoRedisenado';
import { useColumnaResultadosModo } from './shared/useColumnaResultadosModo';
import { CardPicker } from '../components/PanelCatalogoCompacto';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { CeldaAjuste, ValorConChevron } from '../components/AjustesRapidosProducto';
import { ContadorImagenes } from '../components/ContadorImagenes';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import { OpcionesDeFidelidad } from './shared/OpcionesDeFidelidad';
import { nombreCortoModo } from '../modesCatalog';
import { getUserPlan } from '../../../lib/planUtils';
import {
  CANTIDAD_DEFAULT,
  FIDELIDAD_DEFAULT,
  FORMATO_DEFAULT,
  fidelidadPorId,
  formatoMasParecido,
  comoSeGenera,
  promptNivelLibre,
  promptParaDescribir,
  promptReplicar,
  rubroDelPrompt,
  type FidelidadId,
  type ModeloParaPrompt,
} from '../replicarFoto';
import { describirPlantilla, editarReplicando } from '../replicarFotoApi';
import { scrollToAndShake, type Viewport } from '../scrollShake';
import {
  borrarPlantillaPropia,
  fetchPlantillasPropias,
  firmarPlantilla,
  PLANTILLAS_BUCKET,
  subirPlantillaPropia,
  type PlantillaPropia,
} from '../plantillasPropiasApi';
import { MAX_TANDAS_EN_CURSO } from '../generacionesEnCurso';
import { palabraDeModelo } from '../../../lib/nombreDeModelo';
import { leerSaldo } from '../../../lib/saldo';

/** El `path` de la entrada en `MODES` — de acá sale el nombre corto del modo. */
const PATH_MODO = 'replicar-foto';
/** El `kind` del preset: rotula las tandas en la columna de resultados. */
const KIND_MODO = 'replicar-foto';

// Carátula propia: la misma escena dos veces con el producto cambiado. Es APAISADA
// (3:2), no 9:16 como la placeholder que traía — de ahí que los tres usos la limiten
// por ANCHO y no por alto. Desde
// ADR-2026-09-22-cada-modo-con-su-caratula va a sangre y no con los costados
// transparentes, así que acá el `object-contain` la muestra con su propio fondo.
const HERO_IMG = '/images/modo_replicar_foto/caratula.jpg';
const HERO_TITULO = 'La foto que te gustó, hecha de nuevo';
const HERO_DESC =
  'Subí una foto que quieras imitar y Atlas la vuelve a hacer: con el producto de tu marca adentro, o cambiando lo que le pidas.';

/** Cuánto dura la barra de carga de mobile. Una imagen tarda bastante menos que
 *  un video, pero la barra se cierra sola con `completeNow` cuando llega. */
const CARGA_MS = 90_000;

type PanelLateral = 'producto' | 'modelo' | 'plantilla' | 'formato' | 'fidelidad' | null;

/**
 * La grilla de productos es LA MISMA que la de los modos del rediseño (Automática,
 * Modelo, Fotos de producto, Catálogo) —
 * ADR-2026-09-08-la-grilla-de-productos-de-mobile-es-la-misma-en-todos-los-modos.
 *
 * Tres columnas en desktop y dos en mobile, con `nombreSobreLaFoto`: tarjeta sin marco
 * y el nombre en blanco sobre un degradado encima de la foto, en vez del pie blanco que
 * traía el panel por default —que además cortaba los nombres largos en "Nuevo produc…"—.
 */
const COLUMNAS_PRODUCTOS_DESKTOP = 3;
const COLUMNAS_PRODUCTOS_MOBILE = 2;

/**
 * Hasta cuántos productos entran en una foto. Es el tope que acepta el modo `replicar`
 * del backend (`variante_ids`, máx. 4): con 4 productos y 2 vistas cada uno, más la foto
 * y la modelo, son 10 imágenes en el pedido — el techo del proveedor más restrictivo.
 */
const MAX_PRODUCTOS = 4;

export default function ReplicarFotoMode() {
  const navigate = useNavigate();
  const {
    sidebarWidth,
    setMobileBackOverride,
    setMobileHideBottomNav,
    setMobileHeaderTransparent,
  } = useSidebar();
  const volverAtras = useVolverAtras();
  const { rubro, marcaId, loaded: rubroCargado } = useRubroMarcaActiva();
  // Cómo se llama en pantalla la persona de la foto para este rubro
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = palabraDeModelo(rubro);

  // Sin gate de rubro: la ruta la puede abrir cualquier marca
  // (ADR-2026-09-13-replicar-una-foto-sale-del-rubro-belleza). `rubro` se sigue
  // leyendo porque los selectores compartidos (`PickProductPanel`,
  // `SubirProductoDrawer`) lo usan para su copy, y `rubroCargado` porque el fetch
  // de productos espera a que la MARCA resuelva.

  // ── Cuenta ────────────────────────────────────────────────────────────────
  const [companyId, setCompanyId] = useState<string | null>(() =>
    empresaRecordada(),
  );
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      setUserId(auth.user.id);
      // Sólo la empresa: la MARCA activa la resuelve `useRubroMarcaActiva` con el
      // desempate `current_brand_id` → `is_default` → la primera. Leerla acá a mano
      // devuelve `null` en las cuentas que nunca cambiaron de marca —la mayoría—, y
      // con `null` el catálogo de productos vuelve a traer los de TODA la empresa.
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      const perfil = p as { id_company?: string | null } | null;
      setCompanyId(perfil?.id_company ?? null);
      recordarEmpresa(perfil?.id_company, auth.user.id);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // ── Productos ─────────────────────────────────────────────────────────────
  // `PickProductPanel` los lee del context, que normalmente provee `InterfazV2`.
  // Esta pantalla vive afuera, así que los trae y los provee ella — el mismo
  // arreglo que hace `OmniRefAttach` en el editor de video.
  //
  // Los de la MARCA ACTIVA, no los de la empresa: una cuenta con varias marcas tiene
  // en la misma empresa la ropa, los muebles y la comida de las otras, y la foto que
  // se está replicando es de UNA de ellas. Elegir un jean para replicar una foto de
  // cosmética no es una opción que valga la pena ofrecer.
  //
  // `fetchUserProducts` con `brandId` incluye SIEMPRE los productos sin marca
  // (`brand_id IS NULL`): son los que quedaron de un onboarding viejo y no son "de
  // otra marca", son de nadie — esconderlos dejaría a una cuenta sin su catálogo.
  const [productos, setProductos] = useState<MockProductV2[]>([]);
  const [productosCargados, setProductosCargados] = useState(false);

  /** Re-lee el catálogo. Lo llama el drawer de alta cuando termina de subir uno. */
  const refrescarProductos = useCallback(async () => {
    if (!companyId) return;
    try {
      const p = await fetchUserProducts(companyId, marcaId);
      setProductos(p);
    } finally {
      setProductosCargados(true);
    }
  }, [companyId, marcaId]);

  useEffect(() => {
    // Se espera a que la marca resuelva (`rubroCargado`): con `marcaId` todavía en
    // `null` el fetch trae toda la empresa y la grilla parpadearía con productos de
    // otras marcas antes de acotarse.
    if (!companyId || !rubroCargado) return;
    let cancelado = false;
    fetchUserProducts(companyId, marcaId)
      .then((p) => {
        if (cancelado) return;
        setProductos(p);
        setProductosCargados(true);
      })
      .catch(() => {
        if (!cancelado) setProductosCargados(true);
      });
    return () => {
      cancelado = true;
    };
  }, [companyId, marcaId, rubroCargado]);

  // ── Modelos ───────────────────────────────────────────────────────────────
  // Una sola vez por empresa; `fetchUserModels` devuelve las globales de Atlas y las
  // privadas de la cuenta. Se recarga cuando el picker crea o sube una.
  const recargarModelos = useCallback(() => {
    if (!companyId) return;
    fetchUserModels(companyId).then(setModelos).catch(() => {
      /* sin modelos la grilla queda con las globales que ya hubiera */
    });
  }, [companyId]);
  useEffect(() => {
    recargarModelos();
  }, [recargarModelos]);

  // ── Plantillas propias ────────────────────────────────────────────────────
  const [plantillas, setPlantillas] = useState<PlantillaPropia[]>([]);
  const [plantillasCargadas, setPlantillasCargadas] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let cancelado = false;
    fetchPlantillasPropias(companyId)
      .then((p) => {
        if (cancelado) return;
        setPlantillas(p);
        setPlantillasCargadas(true);
      })
      .catch(() => {
        if (!cancelado) setPlantillasCargadas(true);
      });
    return () => {
      cancelado = true;
    };
  }, [companyId]);

  // ── Lo que se elige ───────────────────────────────────────────────────────
  /**
   * MOBILE: en qué pantalla estás. Arranca SIEMPRE en `foto`, aun con una plantilla ya
   * elegida (se vuelve acá con el back, y ahí la card la muestra y aparece "Siguiente")
   * — mismo criterio que la presentación de "Ambiente completo" y "Variantes".
   * Desktop no lo lee: allá el panel es uno solo.
   */
  const [paso, setPaso] = useState<'foto' | 'configurar'>('foto');
  /** Los productos son OPCIONALES, hasta `MAX_PRODUCTOS`: sin ninguno se replica la foto
   *  tal cual (si tiene un producto, se queda). El primero es el principal. */
  const [productosElegidos, setProductosElegidos] = useState<MockProductV2[]>([]);
  /** La modelo, OPCIONAL: `null` es "sin modelo" —la persona de la foto, si hay, se
   *  queda—. Con una, reemplaza a la de la foto (o se suma si no hay ninguna). */
  const [modelo, setModelo] = useState<ModelSelection | null>(null);
  /** Las modelos que la cuenta puede usar: las globales de Atlas + las suyas. */
  const [modelos, setModelos] = useState<ModelV2[]>([]);
  /** La sub-vista del picker de modelo (`list` / `create` / `upload`); la controla el
   *  padre y se resetea al cerrar el panel, como en el modo hermano. */
  const [modelView, setModelView] = useState<ModelPickerView>('list');
  const [plantillaId, setPlantillaId] = useState<string | null>(null);
  /** Lo que llegó de Recrear por `location.state`: una subida (`plantillaId`) o un pin. */
  const estadoDeEntrada = useLocation().state as EstadoDeRecrearImagen | null;
  /**
   * Un pin de Pinterest elegido en Recrear, en vez de una subida
   * (ADR-2026-10-29-pinterest-en-recrear-con-plantillas). Es sólo su link: no se guarda en
   * `plantillas_propias` ni en ningún lado, y al elegir una subida se suelta.
   */
  const [pin, setPin] = useState<PinParaRecrear | null>(() => pinDeEntrada(estadoDeEntrada));
  const [indicaciones, setIndicaciones] = useState('');
  const [fidelidad, setFidelidad] = useState<FidelidadId>(FIDELIDAD_DEFAULT);
  const [formato, setFormato] = useState(FORMATO_DEFAULT);
  /**
   * El formato lo tocó el usuario: desde ahí la sugerencia por proporción deja de
   * pisarlo. Sin esto, elegir otra plantilla le cambiaría el formato que acaba de
   * elegir a mano.
   */
  const formatoTocadoRef = useRef(false);
  const [cantidad, setCantidad] = useState(CANTIDAD_DEFAULT);

  const plantilla = useMemo(
    () => plantillas.find((p) => p.id === plantillaId) ?? null,
    [plantillas, plantillaId],
  );
  /** La foto a replicar, sea una subida o un pin: lo que se muestra en las cards y la muestra. */
  const foto = useMemo(
    () =>
      plantilla
        ? { nombre: plantilla.nombre, imageUrl: plantilla.imageUrl }
        : pin
          ? { nombre: pin.titulo ?? 'Imagen de Pinterest', imageUrl: pin.miniatura }
          : null,
    [plantilla, pin],
  );

  // ── Capas ─────────────────────────────────────────────────────────────────
  const [panelLateral, setPanelLateral] = useState<PanelLateral>(null);
  /** Mobile: los pickers viven en bottom sheets en vez del panel lateral. */
  const [sheetProducto, setSheetProducto] = useState(false);
  const [sheetModelo, setSheetModelo] = useState(false);
  const [sheetPlantilla, setSheetPlantilla] = useState(false);
  const [sheetFormato, setSheetFormato] = useState(false);
  /** Mobile: la fidelidad se elige en una PANTALLA, como los productos. */
  const [sheetFidelidad, setSheetFidelidad] = useState(false);
  /** El alta de un producto corre en un drawer encima, no navegando a otro paso: se
   *  entra por la primera tarjeta de la grilla y se vuelve al mismo lugar. */
  const [subirProductoAbierto, setSubirProductoAbierto] = useState(false);
  /** Plan de la cuenta — el drawer de alta lo usa para el tope de la subida en bloque.
   *  El guard de saldo del Generar NO lo lee: ahí se relee fresco de Supabase. */
  const [plan, setPlan] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    getUserPlan()
      .then((p) => {
        if (!cancelado) setPlan(p);
      })
      .catch(() => {
        /* sin plan el drawer cae a su default */
      });
    return () => {
      cancelado = true;
    };
  }, []);

  // ── Estado de la generación ───────────────────────────────────────────────
  const [subiendo, setSubiendo] = useState(false);
  const [borrando, setBorrando] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const [completeNow, setCompleteNow] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const pendingNavRef = useRef<{ imageIds: string[]; companyId: string } | null>(
    null,
  );
  const planRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // El costo sale de la MISMA fórmula que el resto de las generaciones de imagen
  // (`costoDeGeneracion`), y no de una cuenta propia: acá se cobra por imagen
  // entregada y la resolución la fija el backend (1K), así que es `cantidad`.
  const costo = costoDeGeneracion({ cantidadImagenes: cantidad });

  const nombreModo = nombreCortoModo(PATH_MODO, 'Recrear imagen');

  /**
   * PRIMERA VEZ, SÓLO DESKTOP: el panel de "Foto a replicar" se abre SOLO al entrar si la
   * empresa no subió ninguna plantilla todavía.
   *
   * En MOBILE ya no hace falta y por eso no corre: la pantalla 1 del modo es justo esto
   * —la portada, qué hay que cargar y la card que abre el picker—, y abrir además el
   * sheet encima taparía la explicación que se acaba de dar
   * (ADR-2026-09-14-replicar-foto-mobile-abre-con-la-foto).
   *
   * Sin ninguna foto propia, la card "Elegí o subí una foto" es un callejón: hay que
   * adivinar que se toca para llegar al único gesto que arranca el modo —subir—. Con la
   * grilla ya desplegada, el "Subir plantilla" queda a la vista. En cuanto hay al menos
   * una guardada el modo ya se conoce y el picker vuelve a abrirse sólo a mano, que es
   * como se comporta el resto de los pickers.
   *
   * El ref lo hace UNA sola vez por visita: si el usuario lo cierra sin subir nada, no
   * se le vuelve a abrir en la cara.
   */
  const pickerPlantillaAutoRef = useRef(false);
  useEffect(() => {
    if (pickerPlantillaAutoRef.current) return;
    if (!plantillasCargadas) return;
    if (plantillas.length > 0 || plantillaId || pin) return;
    const enPanel =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches;
    if (!enPanel) return;
    pickerPlantillaAutoRef.current = true;
    setPanelLateral('plantilla');
  }, [plantillasCargadas, plantillas.length, plantillaId, pin]);

  /** Qué falta para poder generar. Es también el texto que va bajo el CTA. Lo único
   *  obligatorio es la foto; los productos son opcionales pero tienen tope. */
  const falta = !foto
    ? 'Elegí o subí la foto que querés replicar'
    : productosElegidos.length > MAX_PRODUCTOS
      ? `Elegiste ${productosElegidos.length} productos y entran hasta ${MAX_PRODUCTOS}: sacá ${productosElegidos.length - MAX_PRODUCTOS}.`
      : null;

  /**
   * El bloque adonde manda el Generar cuando falta algo: la card de la foto, o la de los
   * productos si lo que sobra son productos.
   *
   * El id lleva SUFIJO DE VIEWPORT porque mobile y desktop montan los dos el bloque de
   * configuración a la vez (el de mobile es `md:hidden`, no está desmontado), y
   * `getElementById` se quedaría con el primero del documento —el oculto— dejando el
   * scroll y la sacudida en la nada. Es el mismo motivo por el que el modo Video pasa
   * sus ids sólo al render desktop (ADR-2026-08-21-video-con-el-diseno-nuevo).
   */
  const faltaTargetId = (sufijo: Viewport) =>
    !foto ? `replicar-plantilla-${sufijo}` : `replicar-producto-${sufijo}`;

  // ── Back flotante de mobile ───────────────────────────────────────────────
  useEffect(() => {
    setMobileBackOverride(() => () => {
      // La pantalla de elegir producto tapa al resto (`z-[35]`) y es la que el back tiene
      // que cerrar mientras está abierta: sin esta rama, la flecha salía del modo POR
      // DEBAJO del picker, que quedaba montado tapándolo.
      if (sheetProducto) setSheetProducto(false);
      // Ídem la pantalla de la fidelidad, que se monta igual.
      else if (sheetFidelidad) setSheetFidelidad(false);
      // De la configuración se vuelve a la pantalla de la foto, no se sale del modo.
      else if (paso === 'configurar') setPaso('foto');
      // Y de la PRIMERA pantalla, a donde estabas —
      // ADR-2026-09-07-el-back-de-los-modos-vuelve-a-donde-estabas.
      else volverAtras();
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetProducto, sheetFidelidad, paso, volverAtras]);

  /** Pantalla-TAREA: la barra inferior se aparta mientras el modo está abierto
   *  (ADR-2026-08-05-la-barra-no-va-en-las-pantallas-tarea). */
  useEffect(() => {
    setMobileHideBottomNav(true);
    return () => setMobileHideBottomNav(false);
  }, [setMobileHideBottomNav]);

  /**
   * La pastilla de créditos del header de mobile, arriba a la derecha.
   *
   * El chrome sólo la dibuja con el header en modo transparente (`Sidebar`), que es la
   * condición que usan la home y las pantallas del preset sheet —de ahí que Modelo la
   * tenga y este modo no—. Acá vale lo mismo: el modo ocupa la pantalla entera y lo que
   * se está por hacer cuesta créditos, así que el saldo tiene que estar a la vista antes
   * de empezar, no recién en el botón de Generar.
   */
  useEffect(() => {
    setMobileHeaderTransparent(true);
    return () => setMobileHeaderTransparent(false);
  }, [setMobileHeaderTransparent]);

  // ── La columna de resultados ──────────────────────────────────────────────
  const { columna, hayColumna, visorAbierto, lanzarTanda, puedeCrear } =
    useColumnaResultadosModo({
      companyId,
      brandId: marcaId,
      modo: KIND_MODO,
      onError: setError,
      onLimitePlan: () =>
        setLimitModal(
          planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        ),
    });

  // ── Elegir / quitar productos ─────────────────────────────────────────────
  /**
   * Selección MÚLTIPLE con pie de confirmar, como en Modelo y Catálogo: con varios
   * productos, un toque tiene que poder sumar sin cerrar. (El picker de uno solo elegía
   * con un toque y sin pie; al abrirlo a varios ese gesto dejó de servir —
   * ADR-2026-09-14-replicar-una-foto-con-varios-productos.) `PickProductPanel` devuelve
   * la selección en el orden en que se tocó: el primero es el principal.
   */
  const elegirProductos = (elegidos: MockProductV2[]) => {
    // Se guardan TODOS los que tocó, aunque pasen el tope: `PickProductPanel` no sabe
    // limitar la selección, así que recortar acá dejaría productos afuera sin decir
    // nada. Pasado el tope lo dice `falta` y el Generar sacude la card.
    setProductosElegidos(elegidos);
    setPanelLateral(null);
    setSheetProducto(false);
  };

  const quitarProductos = () => {
    setProductosElegidos([]);
    setError(null);
  };

  const cerrarPickersDeModelo = () => {
    setPanelLateral(null);
    setSheetModelo(false);
    // El picker vuelve a su grilla: reabrirlo no tiene que caer en "Creá tu modelo" a
    // medio llenar.
    setModelView('list');
  };

  /** Elegir cierra el picker, como el de producto: un toque y listo. */
  const elegirModelo = (sel: ModelSelection) => {
    setModelo(sel);
    cerrarPickersDeModelo();
  };

  const quitarModelo = () => setModelo(null);

  /**
   * Cómo viaja la modelo a la generación. Una FOTO (guardada o recién subida) va como
   * referencia `modelo` —el backend la etiqueta "Modelo de referencia:"— y el prompt la
   * nombra por esa etiqueta. Una DESCRITA ("Creá tu modelo") son palabras y van al
   * prompt. Si no se puede resolver la foto se genera SIN modelo, antes que con una
   * referencia rota.
   *
   * "Cargá tu modelo" cae en `preset` y NO en `upload`: `UploadFlow` sube la foto a
   * `modelos/<empresa>/full/` y guarda la fila ANTES de avisar, así que lo que llega es
   * un modelo guardado como cualquier otro. `upload` queda en el tipo `ModelSelection`
   * porque otros montajes del picker lo usan, pero por acá no pasa.
   *
   * La foto se RE-FIRMA acá, y no se usa la `thumbnailUrl` que quedó en la selección:
   * esa se firmó al cargar la lista y dura una hora, así que en una pantalla abierta un
   * rato llega vencida al backend
   * (ADR-2026-09-14-la-foto-del-modelo-se-re-firma-antes-de-generar). Es lo mismo que ya
   * hace `firmarPlantilla` con la foto a replicar.
   */
  const resolverModelo = async (): Promise<{
    url: string | null;
    prompt: ModeloParaPrompt | null;
  }> => {
    if (!modelo) return { url: null, prompt: null };
    if (modelo.mode === 'custom') {
      const texto = descripcionModeloEnPalabras(modelo.fields);
      return { url: null, prompt: texto ? { tipo: 'descripcion', texto } : null };
    }
    if (modelo.mode === 'preset') {
      const m = modelos.find((u) => String(u.id) === modelo.id);
      // Con el modelo en la lista manda la firma NUEVA, y si no sale se genera SIN modelo
      // (ADR-2026-09-15-la-foto-del-modelo-no-cae-a-la-firma-vieja): caer acá a
      // `modelo.imageUrl` sería mandar exactamente la firma vieja que este camino existe
      // para no mandar —la que el backend se come como un 4xx al bajarla—. Sin el modelo en
      // la lista —recién subido, la lista todavía no volvió— sirve la de la selección, que
      // en ese caso se acaba de emitir.
      const url = m ? await firmarFotoDeModelo(m) : (modelo.imageUrl ?? null);
      return { url, prompt: url ? { tipo: 'foto' } : null };
    }
    return { url: null, prompt: null };
  };

  // ── Elegir / subir / borrar plantilla ─────────────────────────────────────
  const cerrarPickersDePlantilla = () => {
    setPanelLateral(null);
    setSheetPlantilla(false);
  };

  /**
   * Sugerencia de formato por la proporción de la foto: el modo promete replicar una foto y
   * generar una referencia apaisada en 4:5 recorta justo la composición que se quería
   * copiar. Se mide la imagen ya cargada; si el usuario tocó el picker de formato, manda lo
   * suyo.
   */
  const sugerirFormato = (url: string | null | undefined) => {
    if (formatoTocadoRef.current || !url) return;
    const img = new Image();
    img.onload = () => {
      if (formatoTocadoRef.current) return;
      setFormato(formatoMasParecido(img.naturalWidth, img.naturalHeight));
    };
    img.src = url;
  };

  const elegirPlantilla = (p: PlantillaPropia) => {
    setPlantillaId(p.id);
    // Una subida reemplaza al pin con el que se haya entrado.
    setPin(null);
    cerrarPickersDePlantilla();
    // La foto es la bisagra de las dos pantallas de mobile: elegirla (o terminar de
    // subirla, que llama acá) pasa sola a la configuración, sin un "Siguiente" de más.
    // En desktop `paso` no se lee (ADR-2026-09-14-replicar-foto-mobile-abre-con-la-foto).
    setPaso('configurar');
    sugerirFormato(p.imageUrl);
  };

  // Entrando con un pin, igual que al elegir una foto: a configurar y el formato del pin
  // (la miniatura de 600 de ancho conserva su proporción).
  const pinDeEntradaAplicadoRef = useRef(false);
  useEffect(() => {
    if (pinDeEntradaAplicadoRef.current || !pin) return;
    pinDeEntradaAplicadoRef.current = true;
    setPaso('configurar');
    sugerirFormato(pin.miniatura);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el MISMO archivo
    if (!file || !companyId) return;
    setSubiendo(true);
    setError(null);
    try {
      const nueva = await subirPlantillaPropia({
        file,
        idCompany: companyId,
        brandId: marcaId,
        cantidadActual: plantillas.length,
      });
      setPlantillas((prev) => [nueva, ...prev]);
      elegirPlantilla(nueva);
    } catch (err) {
      console.error('[ReplicarFotoMode] subir plantilla falló', err);
      setError(
        err instanceof UnsupportedImageFormatError
          ? err.message
          : 'No se pudo subir la plantilla. Intentá nuevamente.',
      );
    } finally {
      setSubiendo(false);
    }
  };

  // Entrando desde Recrear con una foto ya elegida (o recién subida allá), el modo abre
  // con ella puesta — ADR-2026-10-05-recrear-con-todas-subidas-y-subir-imagen. Una sola
  // vez, y recién cuando la colección cargó: antes no hay a quién elegir.
  const plantillaDeEntrada = estadoDeEntrada?.plantillaId;
  const entradaAplicadaRef = useRef(false);
  useEffect(() => {
    if (entradaAplicadaRef.current || !plantillaDeEntrada || !plantillasCargadas) return;
    entradaAplicadaRef.current = true;
    const p = plantillas.find((x) => x.id === plantillaDeEntrada);
    if (p) elegirPlantilla(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantillaDeEntrada, plantillasCargadas, plantillas]);

  const borrarPlantilla = async (p: PlantillaPropia) => {
    if (!companyId || borrando) return;
    setBorrando(p.id);
    try {
      await borrarPlantillaPropia({ id: p.id, path: p.path, idCompany: companyId });
      setPlantillas((prev) => prev.filter((x) => x.id !== p.id));
      if (plantillaId === p.id) setPlantillaId(null);
    } catch (err) {
      console.error('[ReplicarFotoMode] borrar plantilla falló', err);
      setError('No se pudo borrar la plantilla.');
    } finally {
      setBorrando(null);
    }
  };

  // ── Generar ───────────────────────────────────────────────────────────────
  const generar = async () => {
    if (!foto || !userId || !companyId || generando || falta) return;
    /**
     * En DESKTOP la tanda va a la columna de la derecha y la pantalla no se mueve;
     * abajo de `md` no hay dos columnas (el shell es `hidden md:flex`), así que ahí
     * sigue el camino de siempre: pantalla de carga y las imágenes en el historial.
     * Es la misma bifurcación que Animar imagen y Mostrar cómo funciona.
     */
    const enPanel =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches;
    setGenerando(true);
    setError(null);
    if (!enPanel) {
      setShowLoading(true);
      setCompleteNow(false);
    }
    try {
      // Saldo con el dato fresco de Supabase. SIN piso de plan: es un modo de
      // imagen, como Automática o Fotos de producto.
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan')
        .eq('id_company', companyId)
        .single();
      const plan = (perfil as { plan?: string } | null)?.plan;
      planRef.current = plan ?? null;
      const restantes = await leerSaldo(companyId).then((s) => s.disponibles).catch(() => undefined);
      if (typeof restantes === 'number' && restantes < costo) {
        setShowLoading(false);
        setGenerando(false);
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }

      // La plantilla se re-firma acá porque la URL de la grilla puede llevar una hora en
      // pantalla; los productos se resuelven a su foto principal, en el orden elegido.
      // Un pin viaja por su link grande, sin firmar ni copiar: el backend lo baja a memoria
      // (ADR-2026-10-29-pinterest-en-recrear-con-plantillas).
      const [urlPlantilla, urlsProductos] = await Promise.all([
        pin ? Promise.resolve(pin.imagen) : plantilla ? firmarPlantilla(plantilla.path) : Promise.resolve(null),
        Promise.all(productosElegidos.map((p) => getProductFullImageUrl(companyId, p.id))),
      ]);
      if (!urlPlantilla) {
        setShowLoading(false);
        setGenerando(false);
        setError('No se pudo leer la plantilla. Probá con otra.');
        return;
      }
      // Con productos ELEGIDOS cuya foto no se puede leer NO se genera —
      // ADR-2026-09-11-replicar-foto-no-genera-sin-el-producto. El prompt le dice al
      // modelo que esas referencias SON los productos de la marca: sin ellas inventa unos
      // parecidos, y esa tanda se cobra igual. Preferimos no arrancar y decir cuál falla.
      const sinFoto = productosElegidos.filter((_, i) => !urlsProductos[i]);
      if (sinFoto.length > 0) {
        setShowLoading(false);
        setGenerando(false);
        setError(
          `No se pudo leer la foto de ${sinFoto.map((p) => p.name).join(', ')}. Revisá que tenga foto cargada o sacalo de la selección.`,
        );
        return;
      }
      const conProducto = productosElegidos.length > 0;
      const modeloResuelto = await resolverModelo();
      // Moda habla de prendas puestas; el resto, de objetos que se usan o se apoyan
      // (ADR-2026-09-14-replicar-una-foto-habla-del-rubro).
      const rubroPrompt = rubroDelPrompt(rubro);
      /**
       * EL MOTOR (ADR-2026-10-08-dos-opciones-de-fidelidad): "Igual a la foto" SIN persona
       * nueva va por `referencia` con `gpt-image-2.5-sunburst` primero; CON persona va por
       * `editar` y el backend fusiona (`edicionDeReplicar`): Seedream primero, con la
       * corrección del tinte verde. Lo de abajo explica por qué `referencia` declara `editar`
       * (ADR-2026-09-14-replicar-una-foto-genera-con-image-2-5).
       *
       * El nivel alto ya lo tenía: `/v1/images/edit` corre la cadena de EDICIÓN, que
       * arranca ahí. El medio corría la de CREACIÓN —Gemini primero— porque
       * `generateFreeformImage` sin `tarea` cae en el default. Declarando `editar` toma
       * la misma cadena que el nivel alto: 2.5 → Gemini → Seedream, CON red en todas las
       * cuentas. Se eligió esto sobre `provider_override` justamente por la red: el
       * backend vuelve estricto el override en las cuentas `dev_tester`, y ahí un fallo
       * de 2.5 mataba la generación en vez de caer a Gemini.
       *
       * El nivel BAJO ("Inspirada en la foto") va por la cadena de CREACIÓN normal —Gemini
       * (Nano Banana) primero— haya o no producto o modelo
       * (ADR-2026-10-08-inspirada-va-con-nano-banana): ahí se genera de cero desde el texto
       * de la visión, sin la foto, así que no hay nada que editar ni que fusionar.
       */
      const TAREA_PARA_EL_MOTOR = 'editar' as const;
      // Producto y modelo, ETIQUETADOS: el backend antepone "Producto N vista …:" (con
      // `productId` baja las vistas del producto) y "Modelo de referencia:", y los prompts
      // los nombran así. Los comparten los niveles bajo y medio; el alto manda ids.
      const refsProductoYModelo: ReferenciaEtiquetada[] = productosElegidos.map((p, i) => ({
        url: urlsProductos[i] as string,
        tipo: 'producto',
        productId: p.id,
      }));
      if (modeloResuelto.url) {
        refsProductoYModelo.push({ url: modeloResuelto.url, tipo: 'modelo' });
      }

      // TRES PIPELINES para DOS opciones — ver el encabezado de `replicarFoto.ts`
      // (ADR-2026-09-13-replicar-una-foto-tres-pipelines y
      // ADR-2026-10-08-dos-opciones-de-fidelidad): "Igual a la foto" va por `editar` si hay
      // una persona nueva (Seedream) y por `referencia` con la instrucción de Equilibrada si
      // no (Sunburst). Los tres devuelven ids de filas.
      const { pipeline, nivelDelPrompt } = comoSeGenera(fidelidad, !!modeloResuelto.prompt);
      let pedido: () => Promise<{ imageIds: Array<string | number> }>;

      if (pipeline === 'editar') {
        // "Igual a la foto" CON persona nueva: el backend EDITA la foto en un solo pedido
        // (producto, modelo, instrucción y formato) y, con persona, fusiona: Seedream primero.
        // Siempre hay algo que cambiar (la persona). Una imagen por pedido → `cantidad`
        // pedidos en paralelo, cada uno con su fila y su crédito.
        const descripcionModelo =
          modeloResuelto.prompt?.tipo === 'descripcion' ? modeloResuelto.prompt.texto : null;
        const productoIds = productosElegidos.map((p) => p.id);
        const plantillaFuente = pin
          ? { url: pin.imagen }
          : { bucket: PLANTILLAS_BUCKET, path: (plantilla as PlantillaPropia).path };
        pedido = async () => {
          const ids = await Promise.all(
            Array.from({ length: cantidad }, () =>
              editarReplicando({
                companyId,
                userId,
                plantilla: plantillaFuente,
                productoIds,
                modeloUrl: modeloResuelto.url,
                descripcionModelo,
                instruccion: indicaciones,
                aspectRatio: formatSlugToAspect(formato),
                rubro: rubroPrompt,
              }),
            ),
          );
          return { imageIds: ids };
        };
      } else if (pipeline === 'describir') {
        // NIVEL BAJO: la visión describe la foto como un prompt (gratis) y se genera
        // desde ESE texto, sin la foto como referencia — sólo viajan producto y modelo.
        pedido = async () => {
          const descripcion = await describirPlantilla({
            url: urlPlantilla,
            prompt: promptParaDescribir({
              conProducto,
              modelo: modeloResuelto.prompt,
              indicaciones,
              rubro: rubroPrompt,
              cantidadProductos: productosElegidos.length,
            }),
          });
          return generateFreeformImage({
            userId,
            prompt: promptNivelLibre({
              descripcion,
              conProducto,
              modelo: modeloResuelto.prompt,
              rubro: rubroPrompt,
              cantidadProductos: productosElegidos.length,
            }),
            formatSlug: formato,
            cantidad,
            referenceImages: refsProductoYModelo,
            companyId,
            executionId: crypto.randomUUID(),
            modoUtilizado: 'replicar_foto',
            // La cadena de CREACIÓN normal: Gemini (Nano Banana) primero, sin importar si
            // hay producto o modelo (ADR-2026-10-08-inspirada-va-con-nano-banana).
            tarea: 'crear',
          });
        };
      } else {
        // NIVEL MEDIO: la foto viaja como referencia etiquetada ("Imagen de referencia:")
        // y el prompt pide la misma escena adaptando lo que cambie.
        pedido = () =>
          generateFreeformImage({
            userId,
            prompt: promptReplicar({
              // "Igual a la foto" sin persona viaja con la instrucción de Equilibrada.
              fidelidad: nivelDelPrompt,
              indicaciones,
              conProducto,
              modelo: modeloResuelto.prompt,
              rubro: rubroPrompt,
              cantidadProductos: productosElegidos.length,
            }),
            formatSlug: formato,
            cantidad,
            referenceImages: [{ url: urlPlantilla, tipo: 'adjunto' }, ...refsProductoYModelo],
            companyId,
            executionId: crypto.randomUUID(),
            modoUtilizado: 'replicar_foto',
            // Sin persona nueva: conservar, Sunburst primero.
            tarea: TAREA_PARA_EL_MOTOR,
          });
      }

      if (enPanel) {
        // El tope se pregunta ANTES de disparar: acá todavía se puede frenar
        // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
        if (!puedeCrear()) {
          setGenerando(false);
          setError(
            `Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso. Esperá a que termine alguna para pedir otra.`,
          );
          return;
        }
        lanzarTanda({
          total: cantidad,
          creditos: costo,
          resultado: pedido().then((r) => r.imageIds.map(String)),
          errorGenerico: 'No se pudo generar la imagen. Intentá nuevamente.',
        });
        setGenerando(false);
        return;
      }

      const { imageIds } = await pedido();
      pendingNavRef.current = {
        imageIds: imageIds.map(String),
        companyId,
      };
      setCompleteNow(true); // cierra la barra de carga → onCompleted navega
    } catch (e) {
      console.error('[ReplicarFotoMode] generar falló', e);
      setShowLoading(false);
      setGenerando(false);
      setError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'No se pudo generar la imagen. Intentá nuevamente.',
      );
    }
  };

  // ── Piezas compartidas ────────────────────────────────────────────────────

  const errorBox = error ? (
    <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
      {error}
    </div>
  ) : null;

  const heroImg = (className: string) => (
    <CachedImg src={HERO_IMG} alt={`Ejemplo del modo ${nombreModo}`} className={className} />
  );

  /**
   * "Subir plantilla", en sus dos formas: la CELDA cuadrada que encabeza la grilla y la
   * BARRA ancha del empty state. Es el mismo botón —mismo borde punteado, mismo azul—
   * en dos formatos, porque cambia el lugar y no la acción.
   *
   * La celda va como PRIMERA de la grilla, no como botón arriba de ella — el mismo
   * lugar que ocupa "Subir nuevo producto" en la grilla de productos, y por el mismo
   * motivo: subir y elegir una guardada son dos formas de responder la MISMA pregunta.
   */
  const botonSubirPlantilla = (forma: 'celda' | 'barra') => (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={subiendo}
      className={`relative rounded-[13px] overflow-hidden bg-white border-2 border-dashed border-neutral-300 hover:border-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.03)] disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition-all flex items-center justify-center ${
        forma === 'celda'
          ? 'aspect-square flex-col gap-2 px-2'
          : 'w-full flex-row gap-2.5 py-3 px-4'
      }`}
    >
      <span className="shrink-0 w-9 h-9 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.1)] flex items-center justify-center">
        {subiendo ? (
          <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
        ) : (
          <ImagePlus className="w-5 h-5 text-brand-primary" />
        )}
      </span>
      <span
        className={`font-bold text-brand-primary text-center leading-tight ${
          forma === 'celda' ? 'text-[11.5px]' : 'text-[14px]'
        }`}
      >
        {subiendo ? 'Subiendo…' : 'Subir plantilla'}
      </span>
    </button>
  );

  /**
   * EMPTY STATE del picker: la portada del modo + su explicación, con el botón de subir
   * abajo.
   *
   * Sin plantillas guardadas la grilla no tiene nada que mostrar, y este picker es lo
   * PRIMERO que ve quien entra al modo por primera vez —se abre solo, ver
   * `pickerPlantillaAutoRef`—. Así que acá adentro va lo que en desktop vive en la
   * columna de muestra: la foto de ejemplo y qué hace el modo. Conviven los dos, el
   * "qué es esto" y el "subí una", que es lo único que se puede hacer todavía.
   */
  const vacioPlantillas = (
    // Centrado en el picker, vertical y horizontalmente: mientras no hay ninguna
    // plantilla, esto NO es el encabezado de una grilla —es todo el contenido—, así que
    // se planta en el medio del panel en vez de colgar del borde de arriba.
    <div className="min-h-full flex flex-col items-center justify-center text-center py-2">
      {/* Primero QUÉ ES —título y bajada—, después el ejemplo y recién ahí la acción:
          el mismo orden con el que el modo se presenta en la columna de muestra.
          El texto va MÁS ANCHO que la foto —los mismos 440px que usa esa columna—:
          en la columna angosta el título se partía en dos líneas y la bajada en tres. */}
      <div className="w-full max-w-[440px]">
        <h3 className="text-[16px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
          {HERO_TITULO}
        </h3>
        <p className="mt-1 text-[13.5px] text-[#3C3C43]/60 leading-relaxed">
          {HERO_DESC}
        </p>
      </div>
      {/* La foto y el botón, en una columna del ancho de la portada: el botón queda
          EXACTAMENTE tan ancho como la foto. La foto se limita por ANCHO; con la
          carátula apaisada (3:2) la columna puede ser más ancha que los 270px que
          pedía la 9:16 sin comerse el alto del panel. */}
      <div className="mt-4 w-[340px] max-w-full flex flex-col items-center">
        {heroImg('w-full h-auto object-contain rounded-2xl')}
        {/* Separado de la foto: el botón es la acción, no el pie de la imagen. */}
        <div className="mt-5 w-full">{botonSubirPlantilla('barra')}</div>
        <p className="mt-2.5 text-[12.5px] leading-snug text-black/45">
          Puede ser una foto de cualquier marca: lo único que Atlas se queda es la
          escena.
        </p>
      </div>
    </div>
  );

  /**
   * La grilla de plantillas: subir + las guardadas. La MISMA en el panel lateral
   * de desktop y en el sheet de mobile, que es lo que hace que cambiar de
   * plantilla sea el mismo gesto que elegirla.
   *
   * El borrar vive en la tarjeta y no en un modo "editar": son fotos que subió el
   * usuario y borrar una es una corrección, no una operación de administración.
   */
  const grillaPlantillas = (columnas: 2 | 3) =>
    plantillasCargadas && plantillas.length === 0 ? (
      vacioPlantillas
    ) : (
      <>
        {/* DOS columnas en el sheet de mobile y tres en el panel de desktop, como el resto
            de los pickers del modo (productos y modelos hacen lo mismo con `columnasFijas`
            y `columnasGrilla`). A tres, en un teléfono, las plantillas quedaban en celdas de
            ~100px: una foto que hay que RECONOCER para elegirla no se lee a ese tamaño
            (ADR-2026-09-14-las-plantillas-de-mobile-van-de-a-dos). Las clases van literales
            y no interpoladas: Tailwind no emite `grid-cols-${'{n}'}`. */}
        <div className={`grid gap-3 ${columnas === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {botonSubirPlantilla('celda')}
          {plantillas.map((p) => {
            const activa = p.id === plantillaId;
            return (
              <div key={p.id} className="relative group">
                <button
                  type="button"
                  onClick={() => elegirPlantilla(p)}
                  aria-pressed={activa}
                  className={`relative w-full aspect-square rounded-[13px] overflow-hidden bg-neutral-100 transition-shadow ${
                    activa
                      ? 'ring-2 ring-brand-primary'
                      : 'ring-1 ring-black/[0.06] hover:ring-black/[0.16]'
                  }`}
                >
                  <CachedImg
                    src={p.imageUrl}
                    alt={p.nombre}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => borrarPlantilla(p)}
                  disabled={borrando === p.id}
                  aria-label={`Borrar ${p.nombre}`}
                  // Visible SIEMPRE en mobile y sólo al hover en desktop: en un teléfono no hay
                  // hover, así que un `opacity-0 group-hover` deja el borrar inalcanzable —
                  // es la grilla del sheet, la única que hay ahí.
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/45 hover:bg-black/70 text-white grid place-items-center opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-60"
                >
                  {borrando === p.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </>
    );

  /**
   * La card de los productos, OPCIONALES. Con varios, `CardPicker` arma el mosaico con
   * las primeras miniaturas y el "+N" del resto; el valor dice el nombre si es uno y la
   * cuenta si son más.
   *
   * La etiqueta lo dice y el valor vacío es "Sin productos" a secas: no se sabe si la
   * foto tiene un producto (puede ser una modelo sola o un paisaje), así que no se
   * afirma que "se conserva el de la foto". Con productos, la X del `accesorio` los
   * quita a todos sin abrir el picker: tocar la card CAMBIA la selección, la X la SACA.
   */
  const MINIATURAS_EN_CARD = 3;
  const cardProducto = (abrir: () => void, sufijo: Viewport) => (
    <div id={`replicar-producto-${sufijo}`}>
      <CardPicker
        etiqueta="Productos (opcional)"
        valor={
          productosElegidos.length === 0
            ? 'Sin productos'
            : productosElegidos.length === 1
              ? productosElegidos[0].name
              : `${productosElegidos.length} productos`
        }
        imagenes={productosElegidos
          .slice(0, MINIATURAS_EN_CARD)
          .map((p) => p.imageUrl)
          .filter(Boolean)}
        extra={productosElegidos.length > MINIATURAS_EN_CARD ? `+${productosElegidos.length - MINIATURAS_EN_CARD}` : undefined}
        vacio={<Package className="w-6 h-6 text-brand-black/25" />}
        onClick={abrir}
        accesorio={
          productosElegidos.length > 0 ? (
            <button
              type="button"
              onClick={quitarProductos}
              aria-label="Quitar los productos"
              className="shrink-0 w-7 h-7 rounded-full bg-black/[0.06] hover:bg-black/[0.12] text-brand-black/60 grid place-items-center transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : undefined
        }
      />
    </div>
  );

  /** La card de la plantilla elegida, que encabeza la configuración. */
  const cardPlantilla = (abrir: () => void, sufijo: Viewport) => (
    <div id={`replicar-plantilla-${sufijo}`}>
      <CardPicker
        etiqueta="Foto a replicar"
        valor={foto ? foto.nombre : 'Elegí o subí una foto'}
        imagenes={foto?.imageUrl ? [foto.imageUrl] : []}
        vacio={<Images className="w-6 h-6 text-brand-black/25" />}
        onClick={abrir}
      />
    </div>
  );

  /** La miniatura de la modelo elegida, para su card — la misma que usa el modo Modelo. */
  const fotoModelo = (() => {
    if (!modelo) return null;
    if (modelo.mode === 'preset') {
      const m = modelos.find((u) => String(u.id) === modelo.id);
      return m?.thumbUrl || m?.thumbnailUrl || modelo.imageUrl || null;
    }
    if (modelo.mode === 'upload') return modelo.previewUrl;
    return null;
  })();

  /** La card de la modelo, OPCIONAL, con la misma anatomía que la del producto. */
  const cardModelo = (abrir: () => void) => (
    <CardPicker
      etiqueta={`${palabra.titulo} (opcional)`}
      valor={modelo ? modelSelectionLabel(modelo) : `Sin ${palabra.singular}`}
      imagenes={fotoModelo ? [fotoModelo] : []}
      vacio={<User className="w-6 h-6 text-brand-black/25" />}
      onClick={abrir}
      accesorio={
        modelo ? (
          <button
            type="button"
            onClick={quitarModelo}
            aria-label={`Quitar el ${palabra.singular}`}
            className="shrink-0 w-7 h-7 rounded-full bg-black/[0.06] hover:bg-black/[0.12] text-brand-black/60 grid place-items-center transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : undefined
      }
    />
  );

  /**
   * El picker de modelo: EL MISMO componente que el modo Modelo, con "Creá tu modelo",
   * "Cargá tu modelo" y la grilla de guardadas. `hideAuto`: acá el estado por defecto ya
   * es "sin modelo" (la de la foto se queda), y un tile "Automático" al lado diría dos
   * cosas parecidas. Sin `onChangeExtras` / `onMultiEnabledChange`: sin esos handlers no
   * dibuja "Utilizar varios modelos", que en una foto a replicar no entra.
   */
  const contenidoPickerModelo = (columnas: number, proporcion?: '4/5') => (
    <>
      {modelView === 'list' && (
        <p className="text-[13px] text-[#3C3C43]/60 leading-relaxed mb-4">
          Quién aparece en la foto nueva. Reemplaza a la persona de la foto original, o
          se suma si no hay ninguna.
        </p>
      )}
      <ModelPickerInline
        selection={modelo}
        onChange={elegirModelo}
        view={modelView}
        onViewChange={setModelView}
        userModels={modelos}
        userId={userId}
        companyId={companyId}
        onModelCreated={recargarModelos}
        hideLabel
        hideAuto
        grilla
        columnasGrilla={columnas}
        // Sólo escritorio: en el panel lateral las tarjetas 9:16 salían demasiado
        // altas para tres columnas; en 4:5 entran dos filas a la vista.
        proporcionGrilla={proporcion}
      />
    </>
  );

  /**
   * FIDELIDAD: cuánto se copia de la foto subida. Tres opciones y no un continuo —son
   * tres intenciones distintas (calcar, adaptar, inspirarse), ver `replicarFoto.ts`—;
   * arranca en "Igual a la foto" (ADR-2026-10-07-fidelidad-es-un-desplegable).
   *
   * Se elige como los productos y el modelo
   * (ADR-2026-10-07-fidelidad-se-elige-como-producto-y-modelo): una `CardPicker` que abre
   * el panel lateral en escritorio y una pantalla en el teléfono, donde cada opción
   * muestra su ejemplo y qué hace. La miniatura de la card es el ejemplo de la elegida.
   *
   * Va DEBAJO de la card de la plantilla y SIEMPRE visible, haya o no una foto elegida:
   * es una decisión que se puede tomar de entrada.
   */
  const fidelidadElegida = fidelidadPorId(fidelidad);
  const cardFidelidad = (abrir: () => void) => (
    <CardPicker
      etiqueta="Fidelidad"
      valor={fidelidadElegida.nombre}
      imagenes={fidelidadElegida.ejemplo ? [fidelidadElegida.ejemplo] : []}
      vacio={<SlidersHorizontal className="w-6 h-6 text-brand-black/25" />}
      onClick={abrir}
    />
  );

  const elegirFidelidad = (id: FidelidadId) => {
    setFidelidad(id);
    setPanelLateral(null);
    setSheetFidelidad(false);
  };

  /** La bajada del panel de fidelidad, igual en los dos viewports. */
  const bajadaFidelidad =
    'Cuánto se parece el resultado a la foto que subiste. El panel se cierra al elegir.';

  /**
   * Las indicaciones, ÚLTIMAS y a todo el alto que sobre.
   *
   * Van al final, como en Modelo y en Automático: primero se decide, y lo último
   * es afinar con palabras (ADR-2026-09-03-el-paso-2-cierra-con-la-instruccion).
   * Lo que se escriba acá va al FINAL del prompt, así que puede corregir
   * cualquier cosa de la escena replicada.
   */
  const bloqueIndicaciones = (
    <div className="relative flex-1 min-h-[112px] flex flex-col">
      <textarea
        value={indicaciones}
        onChange={(e) => setIndicaciones(e.target.value)}
        placeholder={`Qué querés cambiar de la foto original (el fondo, la luz, que no salga el ${palabra.singular}…)`}
        className="flex-1 w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black placeholder:text-black/35 resize-none transition-colors"
      />
      {/* El mismo historial de instrucciones que el resto de los modos
          (ADR-2026-08-12-historial-instrucciones-reusables). El `companyId` va
          explícito: esta pantalla vive afuera del `AgentEnvContext`. */}
      <PromptRecientesButton
        tipo="instruccion"
        companyId={companyId}
        onPick={setIndicaciones}
        titulo="Instrucciones usadas antes"
        className="absolute bottom-2.5 right-2.5"
      />
    </div>
  );

  /**
   * Formato, como celda del pie (ADR-2026-09-04-la-duracion-es-una-celda-del-pie).
   *
   * Va SOLA en su fila: la cantidad de imágenes, que la acompañaba, se mudó al lado del
   * Generar — ver `pieGenerar`.
   */
  const celdasAjustes = (abrirFormato: () => void) => (
    <div className="shrink-0 grid grid-cols-2 gap-2">
      <CeldaAjuste etiqueta="Formato" onClick={abrirFormato} abrePanelLateral solo>
        <ValorConChevron texto={getFormatLabel(formato)} />
      </CeldaAjuste>
    </div>
  );

  const elegirFormato = (id: string) => {
    formatoTocadoRef.current = true;
    setFormato(id);
    setPanelLateral(null);
    setSheetFormato(false);
  };

  /**
   * El Generar. Con algo sin elegir NO se apaga: se atenúa y, al tocarlo, scrollea hasta
   * el bloque que falta y lo sacude — el mismo gesto que el Generar del modo Video con
   * influencer (ADR-552 / ADR-553). Un botón muerto obliga a adivinar cuál de los
   * ajustes es el que le falta; éste lo señala.
   *
   * Por eso `aria-disabled` en vez de `disabled`: apagado de verdad no recibiría el
   * click. `disabled` queda sólo para mientras genera, que ahí sí no hay nada que decir.
   */
  const botonGenerar = (sufijo: Viewport) => (
    <button
      type="button"
      onClick={() => {
        if (falta) {
          scrollToAndShake(faltaTargetId(sufijo));
          return;
        }
        void generar();
      }}
      aria-disabled={!!falta}
      disabled={generando}
      className={`flex-1 min-w-0 inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors ${
        falta ? 'opacity-40' : ''
      }`}
    >
      <Sparkles className="w-5 h-5" />
      Generar
      <CreditBadge credits={costo} tone="on-primary" size="sm" />
    </button>
  );

  /** El bloque de configuración: lo comparten el panel de desktop y el de mobile.
   *  Lo único que cambia entre los dos es CÓMO se abren los pickers. */
  /**
   * El ORDEN es el del pedido (ADR-2026-09-13-replicar-una-foto-orden-modelo-y-referencias-etiquetadas):
   * primero la foto a replicar, que es lo único obligatorio y lo que define todo lo
   * demás; debajo cuánto copiarla; después lo opcional —producto y modelo—; y al final
   * las indicaciones, que afinan con palabras. Formato NO está acá: va pegado al pie,
   * fuera del scroll, en los dos viewports.
   */
  const bloqueConfigurar = (
    abrirPlantilla: () => void,
    abrirProducto: () => void,
    abrirModelo: () => void,
    abrirFidelidad: () => void,
    sufijo: Viewport,
  ) => (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      {cardPlantilla(abrirPlantilla, sufijo)}
      {cardFidelidad(abrirFidelidad)}
      {cardProducto(abrirProducto, sufijo)}
      {cardModelo(abrirModelo)}
      {bloqueIndicaciones}
      {errorBox}
    </div>
  );

  /**
   * El pie: el contador de imágenes a la IZQUIERDA del Generar, y debajo lo que falta
   * —que no mueve el botón de lugar—.
   *
   * El contador no va entre los ajustes sino acá, que es el mismo lugar que ocupa en
   * Automática y en Modelo (ADR-2026-08-28-automatico-contador-junto-al-generar): es el
   * único control que cambia lo que CUESTA la tanda, así que se decide con el precio a
   * la vista. La caja gris y el `self-stretch` son los mismos con los que
   * `PickProductPanel` lo monta en su footer, para que los dos pies se lean igual.
   *
   * `[&>span]:mt-0`: `ContadorImagenes` trae un `mt-1` propio —de cuando vivía debajo de
   * la etiqueta de una celda— que en una caja centrada lo descuelga del botón de al
   * lado. Se neutraliza desde afuera, sin tocar el componente.
   */
  const pieGenerar = (clases: string, sufijo: Viewport) => (
    <div className={clases}>
      <div className="flex items-center gap-2">
        <div className="shrink-0 rounded-2xl bg-black/[0.035] px-2 self-stretch flex items-center [&>span]:mt-0">
          <ContadorImagenes
            valor={cantidad}
            max={FREEFORM_MAX_CANTIDAD}
            onChange={setCantidad}
            // Mismo criterio que el CTA de al lado: mientras falte algo, se ve pero no
            // se toca.
            disabled={!!falta}
          />
        </div>
        {botonGenerar(sufijo)}
      </div>
      {falta && !generando && (
        <p className="mt-2 text-[13px] text-black/50 px-1">{falta}</p>
      )}
    </div>
  );

  // ── DESKTOP: lo que va adentro del shell ──────────────────────────────────

  /**
   * El prop set de la grilla de productos del rediseño, en un solo lugar: lo comparten
   * los DOS montajes del picker que tiene el modo (la pantalla de mobile y el panel
   * lateral de desktop, los dos abiertos desde la card del producto).
   *
   * `ocultarBloqueSubida` + `onSubirProducto` van de a dos: el primero saca el bloque
   * azul de "Subir nuevo producto" de arriba de la grilla —que empujaba los productos
   * fuera de la pantalla— y el segundo lo devuelve como PRIMERA TARJETA de la grilla,
   * que es de donde sale un producto nuevo en los otros modos. Sin el segundo, una
   * cuenta que ya tiene productos se queda sin ninguna entrada al alta.
   */
  const propsGrillaProductos = (columnas: number) =>
    ({
      columnasFijas: columnas,
      nombreSobreLaFoto: true,
      ocultarBloqueSubida: true,
      onSubirProducto: () => setSubirProductoAbierto(true),
    }) as const;

  const panelConfigurar = (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-5 pb-4 flex flex-col">
        {bloqueConfigurar(
          () => setPanelLateral('plantilla'),
          () => setPanelLateral('producto'),
          () => setPanelLateral('modelo'),
          () => setPanelLateral('fidelidad'),
          'desktop',
        )}
      </div>
      {/* Formato, PEGADO AL PIE y fuera del scroll: es el último ajuste antes de
          generar y el que menos se toca, y así queda siempre a la vista junto al
          Generar, sin depender de hasta dónde se scrolleó. La línea separadora va
          ACÁ, arriba de Formato, y no en el pie: Formato y el Generar son un solo
          bloque; con la línea entre los dos, Formato quedaba flotando en el scroll. */}
      <div className="shrink-0 px-6 pt-3 bg-white border-t border-black/[0.05]">
        {celdasAjustes(() => setPanelLateral('formato'))}
      </div>
      {pieGenerar('shrink-0 px-6 pt-3 pb-4 bg-white', 'desktop')}
    </>
  );

  const tituloPanelLateral =
    panelLateral === 'plantilla'
      ? 'Foto a replicar'
      : panelLateral === 'formato'
        ? 'Formato'
        : panelLateral === 'fidelidad'
          ? 'Fidelidad'
        : panelLateral === 'modelo'
          ? palabra.titulo
          : 'Productos';

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      {/* MOBILE: elegir el producto es una PANTALLA, no un bottom sheet
          (ADR-2026-09-10-en-mobile-elegir-el-producto-es-una-pantalla).

          Es el mismo montaje que el paso `pick-product` de `PresetDetailSheet` y del modo
          Modelo: `z-[35]` queda encima del contenido del modo (la pantalla de mobile es
          `z-30`) pero DEBAJO del chrome del Sidebar (`z-50`), así que la flecha de volver
          y la pastilla de créditos siguen visibles y clickeables arriba — este modo ya
          prende `mobileHeaderTransparent` y `mobileBackOverride` para todas sus
          pantallas. De ahí el `paddingTop`: el alto de esa top bar (3.5rem) más el notch.

          Sin portal, a diferencia de los otros dos: los overlays de este modo cuelgan
          directo del árbol de la página —no de un sheet con su propio stacking context—,
          igual que la pantalla de configuración de al lado.

          Selección múltiple con pie de confirmar, hasta `MAX_PRODUCTOS`. */}
      {sheetProducto && (
        <div
          className="md:hidden fixed inset-0 z-[35] bg-white flex flex-col page-slide-in-right"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <PickProductPanel
            variant="desktop"
            // La flecha la pone el chrome de arriba: el link "Volver" del panel sería la
            // segunda salida de la misma pantalla.
            hideBack
            // El alto es poco: el encabezado se va con el scroll y deja el buscador
            // pegado arriba. (Antes iba `hideHeader` porque el título lo ponía el sheet;
            // en una pantalla completa, sin él la grilla arranca sin decir qué se elige.)
            scrollHeader
            title="Elegí los productos"
            description={`Los que van a entrar en la foto nueva, hasta ${MAX_PRODUCTOS}. Es opcional.`}
            initialSelectedIds={productosElegidos.map((p) => p.id)}
            onConfirm={elegirProductos}
            onBack={() => setSheetProducto(false)}
            rubro={rubro}
            confirmLabel="Usar estos productos"
            footerSiempreVisible
            // De a dos por fila en mobile, como el resto de los modos.
            {...propsGrillaProductos(COLUMNAS_PRODUCTOS_MOBILE)}
          />
        </div>
      )}
      {/* MOBILE: la fidelidad también es una PANTALLA, con el mismo montaje que la de
          los productos (ver arriba): el back del chrome la cierra. */}
      {sheetFidelidad && (
        <div
          className="md:hidden fixed inset-0 z-[35] bg-white flex flex-col page-slide-in-right"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <div className="shrink-0 px-5 pb-3">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              Fidelidad
            </h2>
            <p className="pt-1 text-[13px] text-[#3C3C43]/60 leading-relaxed">
              {bajadaFidelidad}
            </p>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4">
            <OpcionesDeFidelidad value={fidelidad} onElegir={elegirFidelidad} columnas={1} />
          </div>
        </div>
      )}
      <OptionSheet
        open={sheetPlantilla}
        onClose={() => setSheetPlantilla(false)}
        title="Foto a replicar"
        hideAccept
      >
        {/* La bajada sólo cuando ya hay plantillas: el empty state trae su propia
            explicación —la portada del modo— y las dos juntas se pisan. */}
        {plantillas.length > 0 && (
          <p className="text-[13px] text-[#3C3C43]/60 mb-3">
            Subí la foto que querés imitar o elegí una de las que ya subiste.
          </p>
        )}
        {grillaPlantillas(2)}
        {errorBox}
      </OptionSheet>
      <OptionSheet
        open={sheetFormato}
        onClose={() => setSheetFormato(false)}
        title="Formato"
        hideAccept
      >
        <FormatPickerGrid selectedId={formato} onPick={elegirFormato} columnas={2} />
      </OptionSheet>
      <OptionSheet open={sheetModelo} onClose={cerrarPickersDeModelo} title={palabra.titulo} hideAccept>
        {contenidoPickerModelo(2)}
      </OptionSheet>
      {showLoading && (
        <LoadingAnimation
          message="Generando imagen…"
          subMessage="Esto puede tardar un momento."
          durationMs={CARGA_MS}
          completeNow={completeNow}
          onCompleted={() => {
            setShowLoading(false);
            setGenerando(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: pend.imageIds, companyId: pend.companyId },
              });
            }
          }}
        />
      )}
      {/* El alta de producto, desde la primera tarjeta de la grilla. Al terminar
          refresca el catálogo y el producto nuevo aparece ahí mismo. */}
      <SubirProductoDrawer
        open={subirProductoAbierto}
        onClose={() => setSubirProductoAbierto(false)}
        userCompanyId={companyId}
        brandId={marcaId}
        rubro={rubro}
        userPlan={plan}
        onProductsRefreshed={() => {
          void refrescarProductos();
        }}
      />
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );

  return (
    <ProductsContext.Provider value={productos}>
      <ProductsLoadedContext.Provider value={productosCargados}>
        {/* ── MOBILE: DOS pantallas, la del modo y la configuración —
            ADR-2026-09-14-replicar-foto-mobile-abre-con-la-foto. La misma anatomía que
            "Ambiente completo" y "Variantes de acabado". */}
        {paso === 'foto' ? (
          // ── Pantalla 1: qué foto se replica ─────────────────────────────────
          // `PasoUnoMobile` es la forma con la que abren todos los modos en el teléfono
          // (ADR-2026-09-08-todos-los-modos-abren-igual-en-mobile): portada, consigna y
          // UNA card al pie. Acá pregunta por la FOTO —lo único obligatorio— y no por el
          // producto, que es opcional y se elige en la pantalla siguiente.
          <PasoUnoMobile
            titulo="Elegí la foto a replicar"
            bajada="Subí la que querés imitar o elegí una de las que ya subiste. Después decidís qué cambiarle."
            portada={heroImg(
              'max-w-[400px] max-h-full w-full h-auto object-contain rounded-2xl',
            )}
            Icono={Images}
            // Con una plantilla ya elegida —se vuelve acá con el back— la card muestra
            // ESA foto: una card que sigue diciendo "elegí" desmentiría lo que pasó.
            miniaturas={
              foto?.imageUrl ? (
                <span className="block h-[52px] w-[52px] shrink-0 overflow-hidden rounded-xl bg-white">
                  <CachedImg
                    src={foto.imageUrl}
                    alt={foto.nombre}
                    className="w-full h-full object-cover"
                  />
                </span>
              ) : undefined
            }
            cardTexto={foto ? foto.nombre : 'Elegí o subí una foto'}
            onAbrir={() => setSheetPlantilla(true)}
            // Elegir una foto ya pasa solo a configurar; este botón es la salida para
            // quien volvió atrás y no quiere reabrir el picker para tocar lo mismo.
            onSiguiente={foto ? () => setPaso('configurar') : undefined}
            aviso={errorBox}
          />
        ) : (
          // ── Pantalla 2: qué se le cambia ────────────────────────────────────
          <div
            className="md:hidden fixed inset-0 z-30 bg-white flex flex-col page-slide-in-right"
            // El chrome del shell dibuja el back y la pastilla de créditos FLOTANDO
            // arriba, en 3.5rem más el safe area del notch. Con `pt-16` (64px fijos) el
            // título se metía debajo del back en cualquier teléfono con notch.
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' }}
          >
            {/* La consigna del paso, FUERA del scroll y en los 19px que usan todos los
                modos (Variantes, Ambiente completo, Mostrar cómo funciona…). Antes era el
                nombre del modo en 22px: más grande que en el resto de la app y, además,
                repetía lo que ya dice la pantalla anterior. */}
            <div className="shrink-0 px-5 pb-3">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                Qué cambia de la foto
              </h2>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4 flex flex-col gap-4">
              {bloqueConfigurar(
                () => setSheetPlantilla(true),
                () => setSheetProducto(true),
                () => setSheetModelo(true),
                () => setSheetFidelidad(true),
                'mobile',
              )}
            </div>
            {/* UN solo pie —formato y Generar juntos, separados por un gap— en vez de dos
                bloques con su propio padding cada uno: así son 12px arriba y el safe area
                abajo, y no los ~45px de relleno apilado que dejaba el pie partido. El
                safe area del home indicator va ACÁ y no en el contenedor de arriba, para
                no contarlo dos veces. */}
            <div
              className="shrink-0 bg-white px-5 pt-3 border-t border-black/[0.06] flex flex-col gap-2.5"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
            >
              {celdasAjustes(() => setSheetFormato(true))}
              {pieGenerar('', 'mobile')}
            </div>
          </div>
        )}

        {/* ── DESKTOP: el marco y las dos columnas los pone el shell compartido ── */}
        <ShellModoRedisenado
          sidebarWidth={sidebarWidth}
          // Dos niveles, como el resto de los modos: la home y el modo. El paso NO
          // se suma como tercer nivel
          // (ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso).
          migas={[
            { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
            { label: nombreModo },
          ]}
          titulo={nombreModo}
          panel={panelConfigurar}
          panelLateral={({ contenedorRef, offsetIzquierdo }) => (
            <PanelLateralModo
              abierto={panelLateral !== null}
              onCerrar={() => {
                setPanelLateral(null);
                setModelView('list');
              }}
              titulo={tituloPanelLateral}
              offsetIzquierdo={offsetIzquierdo}
              contenedorRef={contenedorRef}
            >
              {panelLateral === 'producto' ? (
                // FUERA de la caja scrolleable de abajo: el picker arma su layout sobre un
                // `h-full` con la grilla scrolleando adentro y el pie de "Usar estos
                // productos" fijo abajo. Metido en una caja con `overflow-y-auto` (y sin
                // `flex`) ese alto no se acota, la grilla se estira entera y el pie queda al
                // final del scroll. Mismo wrapper que el panel lateral de Ambiente completo.
                <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
                  <PickProductPanel
                    variant="desktop"
                    hideBack
                    hideHeader
                    initialSelectedIds={productosElegidos.map((p) => p.id)}
                    onConfirm={elegirProductos}
                    onBack={() => setPanelLateral(null)}
                    rubro={rubro}
                    confirmLabel="Usar estos productos"
                    footerSiempreVisible
                    {...propsGrillaProductos(COLUMNAS_PRODUCTOS_DESKTOP)}
                    pistasDeUso
                  />
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                  {panelLateral === 'plantilla' ? (
                    <>
                      {/* Ídem el sheet de mobile: con la grilla vacía manda el empty
                          state, que ya dice qué es el modo. */}
                      {plantillas.length > 0 && (
                        <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                          Subí la foto que querés imitar o elegí una de las que ya
                          subiste. El panel se cierra al elegir.
                        </p>
                      )}
                      {grillaPlantillas(3)}
                      {errorBox}
                    </>
                  ) : panelLateral === 'fidelidad' ? (
                    <>
                      <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                        {bajadaFidelidad}
                      </p>
                      <OpcionesDeFidelidad
                        value={fidelidad}
                        onElegir={elegirFidelidad}
                        columnas={2}
                      />
                    </>
                  ) : panelLateral === 'modelo' ? (
                    contenidoPickerModelo(3, '4/5')
                  ) : panelLateral === 'formato' ? (
                    <>
                      <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                        Se sugiere solo el que más se parece a la foto que elegiste.
                      </p>
                      <FormatPickerGrid
                        selectedId={formato}
                        onPick={elegirFormato}
                        columnas={3}
                      />
                    </>
                  ) : null}
                </div>
              )}
            </PanelLateralModo>
          )}
          // Con resultados en la columna, ésta se sale de los paddings y llega al
          // marco (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco).
          muestraAlBorde={hayColumna}
          // El visor de una creación es un lightbox portaleado al body: sin bajarle
          // el z a este panel queda debajo y sólo se ve su velo.
          cederZIndex={visorAbierto}
          muestra={
            columna ?? (
              <div
                key={foto ? 'muestra-plantilla' : 'muestra-hero'}
                className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 animate-paso-adelante"
              >
                {foto ? (
                  <>
                    {/* Con la plantilla elegida, la muestra ES la foto a replicar:
                        es lo que el resultado tiene que parecerse. */}
                    <div className="shrink-0 text-center max-w-[440px] mb-3">
                      <p className="text-[13px] font-semibold uppercase tracking-wide text-black/40">
                        La foto a replicar
                      </p>
                    </div>
                    <CachedImg
                      src={foto.imageUrl}
                      alt={foto.nombre}
                      className="max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl"
                    />
                  </>
                ) : (
                  <>
                    {/* El texto ENCABEZA la muestra en vez de ir de pie de foto — el
                        mismo tratamiento de Modelo v2 y Catálogo
                        (ADR-2026-08-21-modelo-v2-la-muestra-se-queda). */}
                    <div className="shrink-0 text-center max-w-[440px] mb-3">
                      <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                        {HERO_TITULO}
                      </h2>
                      <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                        {HERO_DESC}
                      </p>
                    </div>
                    {heroImg(
                      'max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl',
                    )}
                  </>
                )}
              </div>
            )
          }
        />
        {overlays}
      </ProductsLoadedContext.Provider>
    </ProductsContext.Provider>
  );
}
